/**
 * RiderDeductionFilterSection Component
 * 협력사 주정산내역 검색 필터 섹션
 */
import React, { useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import MultiSelectDropdown from '@/shared/components/forms/MultiSelectDropdown';
import { Supplier } from '@/features/partner/supplierStatus/types/supplier.types';
import { generateFridayOptions, getSettlementPeriodForFriday, formatDate } from '@/shared/utils/weeklySettlementDateUtils';
import '@/shared/styles/search-form.css';
import '../styles/PartnerSettlementHistory.css';

interface FilterSectionProps {
    filters: {
        acctStDate: string;
        acctEdDate: string;
        suppId: string;
    };
    suppliers: Supplier[];
    isSupplierUser: boolean;
    onFilterChange: (filters: { acctStDate: string; acctEdDate: string; suppId: string }) => void;
    onSearch: () => void;
    onReset: () => void;
    onExcelDownload: () => void;
}

const FilterSection: React.FC<FilterSectionProps> = ({
                                                         filters,
                                                         suppliers,
                                                         isSupplierUser,
                                                         onFilterChange,
                                                         onSearch,
                                                         onReset,
                                                         onExcelDownload
                                                     }) => {
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    const fridayOptions = useMemo(() => generateFridayOptions(), []);

    const [selectionMode, setSelectionMode] = React.useState<'friday' | 'custom'>('friday');
    const [selectedFridays, setSelectedFridays] = React.useState<string[]>([]);

    const handleFridaysChange = (fridayValues: string[]) => {
        setSelectedFridays(fridayValues);

        if (fridayValues.length > 0) {
            const periods = fridayValues.map(fValue => {
                const friday = new Date(fValue);
                return getSettlementPeriodForFriday(friday);
            });

            const startDate = periods.reduce((earliest, period) =>
                period.startDate < earliest ? period.startDate : earliest, periods[0].startDate);
            const endDate = periods.reduce((latest, period) =>
                period.endDate > latest ? period.endDate : latest, periods[0].endDate);

            onFilterChange({
                ...filters,
                acctStDate: formatDate(startDate),
                acctEdDate: formatDate(endDate)
            });
        }
    };

    const settlementPeriodText = useMemo(() => {
        if (!filters.acctStDate || !filters.acctEdDate) return '';

        const startDate = new Date(filters.acctStDate);
        const endDate = new Date(filters.acctEdDate);

        return `${startDate.getMonth() + 1}월 ${startDate.getDate()}일 ~ ${endDate.getMonth() + 1}월 ${endDate.getDate()}일`;
    }, [filters.acctStDate, filters.acctEdDate]);

    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.id,
            label: supplier.name
        }));
    }, [suppliers]);

    return (
        <div className="card search-card partner-settlement-filter">
            <div className="card-body">
                <div className={`search-form settlement-search-form ${isSupplierUser ? 'supplier-view' : ''}`}>
                    {/* 조회일자 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <div className="filter-label-row">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회일자
                                </label>

                                {settlementPeriodText && (
                                    <span className="filter-period-badge">
                                        <i className="fas fa-calendar-check" />
                                        {settlementPeriodText}
                                    </span>
                                )}
                            </div>

                            <div className="filter-mode-row">
                                {/* 선택 모드 토글 */}
                                <div className="filter-mode-toggle-group">
                                    <button
                                        type="button"
                                        onClick={() => setSelectionMode('friday')}
                                        className={`filter-mode-btn${selectionMode === 'friday' ? ' filter-mode-btn--active' : ''}`}
                                    >
                                        금요일
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setSelectionMode('custom')}
                                        className={`filter-mode-btn${selectionMode === 'custom' ? ' filter-mode-btn--active' : ''}`}
                                    >
                                        직접입력
                                    </button>
                                </div>

                                {/* 금요일 선택 모드 */}
                                {selectionMode === 'friday' && (
                                    <div className="filter-date-selector-wrapper">
                                        <MultiSelectDropdown
                                            options={fridayOptions.map(option => ({
                                                value: option.value,
                                                label: option.label,
                                                description: option.settlementPeriod
                                            }))}
                                            selectedValues={selectedFridays}
                                            onChange={handleFridaysChange}
                                            placeholder="금요일을 선택하세요..."
                                        />
                                    </div>
                                )}

                                {/* 직접 입력 모드 */}
                                {selectionMode === 'custom' && (
                                    <div className="date-range-inputs filter-date-selector-wrapper">
                                        <DateInput
                                            id="filterAcctStDate"
                                            value={filters.acctStDate}
                                            onChange={(value) => onFilterChange({ ...filters, acctStDate: value })}
                                            onKeyPress={handleKeyPress}
                                        />
                                        <span className="date-separator">~</span>
                                        <DateInput
                                            id="filterAcctEdDate"
                                            value={filters.acctEdDate}
                                            onChange={(value) => onFilterChange({ ...filters, acctEdDate: value })}
                                            onKeyPress={handleKeyPress}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 협력사 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-building" />
                                협력사
                            </label>
                            <SearchableSelect
                                options={supplierOptions}
                                value={filters.suppId}
                                onChange={(value) => onFilterChange({ ...filters, suppId: value })}
                                placeholder="협력사 검색..."
                                disabled={isSupplierUser}
                                showAllOption={true}
                                allOptionLabel="전체"
                            />
                        </div>
                    </div>

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        <button className="btn btn-secondary" onClick={onReset}>
                            <i className="fas fa-redo" />
                            초기화
                        </button>
                        {!isSupplierUser && (
                            <button className="btn btn-success" onClick={onExcelDownload}>
                                <i className="fas fa-file-excel" />
                                엑셀
                            </button>
                        )}
                        <button
                            className="btn btn-primary"
                            onClick={onSearch}
                        >
                            <i className="fas fa-search" />
                            검색
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
